import React, { useEffect, useState, Fragment, useRef } from "react";
import Icon from "../Icon/Icon";
import './ModifyCreation.scss'
import { areTagsEqual, capitalizeFirstLetter } from "../../../helpers/utilities";
import { useDispatch, useSelector } from "react-redux";
import { fetchHeatmapTags } from "../../Reducers/actions/heatmapActions";
import { fetchCreationById, reloadCreation, updateCreation } from "../../Reducers/actions/creationsActions";
import { useNavigate, useParams } from "react-router-dom";
import MainEdit from "./MainEdit";
import EditSizes from "./EditSizes";
import NewsEditor from "../NewsEditor/NewsEditor";
import { supabase } from "../../../supabase";
import { usePreviousLocation } from "../../../helpers/hooks";
import { handleGoBackRedirection } from "../Layout/Layout";
import { cachingService } from "../../../helpers/cache";
import { calculateFullPrice } from "../../../helpers/money";
import PriceDetailsAddCreation from "../../components/PriceDetails/PriceDetailsAddCreation";
import ShippingMethodSelector from "../ShippingMethodSelector/ShippingMethodSelector";
import { HYK_FEE_PERCENT, STRIPE_FEE_PERCENT, STRIPE_FIXED_FEE } from "../../constants/globals";
import useAutoGrowTextarea from "../../hooks/useAutoGrowTextarea";
const MAXIMUM_LENGTH = 1000

// Helper function to extract media from array or object
const getMedia = (rawMedia) => {
    if (!rawMedia) return null;
    return Array.isArray(rawMedia) && rawMedia.length > 0 
        ? rawMedia[0] 
        : (rawMedia && !Array.isArray(rawMedia) ? rawMedia : null);
};

const EditFormat = ({ creation, handleSaveFormat, onClose }) => {
    const media = getMedia(creation?.media);
    const [format, setFormat] = useState(media?.media_format);

    return (
        <div className="add-creation__content modify-subscreen pt-40">
            <div className="d-flex aic jcc modify-header mb-8">
                <Icon alias={"chevron-left"} width={24} color="#0A0F1E" className={"p-absolute"} style={{ left: 16 }} onClick={onClose} />
                <span className="fg-black fw-bold fs-sm2">FORMAT</span>
                <button
                    className="btn btn-sm red-btn rounded-xl fs-xxs p-fixed px-16" style={{ right: '16px' }}
                    disabled={!format || format === getMedia(creation?.media)?.media_format}
                    onClick={() => { handleSaveFormat({ name: 'format', value: format }); onClose() }}
                >
                    Save
                </button>
            </div>
            <div className="add-creation__format-options mt-40 mx-16">
                <button
                    className={`add-creation__format-option ${format === 'digital' ? 'active' : ''}`}
                    onClick={() => setFormat('digital')}
                >
                    <div className="add-creation__format-icon">
                        <Icon alias={'digital_format'} width={40} color={format === 'digital' ? '#FFF' : '#FF3232'} />
                    </div>
                    <span className='fs-real fw-bold'>Digital</span>
                </button>
                <button
                    className={`add-creation__format-option ${format === 'physical' ? 'active' : ''}`}
                    onClick={() => setFormat('physical')}
                >
                    <div className="add-creation__format-icon">
                        <Icon alias={'physical_format'} width={37} color={format === 'physical' ? '#FFF' : '#FF3232'} />
                    </div>
                    <span className='fs-real fw-bold'>Physical</span>
                </button>
            </div>
        </div>
    )
}

const CulturalArea = ({ creation, tags, handleSaveCulturalArea, onClose }) => {

    const [currentTag, setCurrentTag] = useState(creation?.tag);

    return (
        <div className="add-creation__content modify-subscreen pt-40">
            <div className="d-flex aic jcc modify-header mb-8">
                <Icon alias={"chevron-left"} width={24} color="#0A0F1E" className={"p-absolute"} style={{ left: 16 }} onClick={onClose} />
                <span className="fg-black fw-bold fs-sm2">CULTURAL AREA</span>
                <button
                    className="btn btn-sm red-btn rounded-xl fs-xxs p-fixed px-16" style={{ right: '16px' }}
                    disabled={!currentTag || currentTag === creation?.tag}
                    onClick={() => { handleSaveCulturalArea({ name: 'tag', value: currentTag }); onClose(); }}
                >
                    Save
                </button>
            </div>
            <div className="cultural-areas d-flex f-column aic jcc mx-16">
                <p className='fs-xxs fw-medium fg-dark mb-12 mt-0'>CULTURAL AREAS</p>
                <div className='d-flex aic jcc area-container'>
                    {tags.filter(tag => tag.is_primary).map(tag => (
                        <span
                            key={tag.tag_id}
                            className={`area tt-uppercase fs-tiny fw-extra-bold ${currentTag === tag.tag_name ? 'fg-white bg-dark' : 'fg-dark bg-white'}`}
                            onClick={() => setCurrentTag(tag.tag_name)}
                        >
                            {capitalizeFirstLetter(tag.tag_name)}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    )
}

const Title = ({ creation, handleSaveTitle, onClose }) => {

    const [currentTitle, setCurrentTitle] = useState(creation?.title)

    return (
        <div className="add-creation__content modify-subscreen pt-40">
            <div className="d-flex aic jcc modify-header mb-8">
                <Icon alias={"chevron-left"} width={24} color="#0A0F1E" className={"p-absolute"} style={{ left: 16 }} onClick={onClose} />
                <span className="fg-black fw-bold fs-sm2">TITLE</span>
                <button
                    className="btn btn-sm red-btn rounded-xl fs-xxs p-fixed px-16" style={{ right: '16px' }}
                    disabled={!currentTitle.length || currentTitle === creation?.title}
                    onClick={() => {
                        handleSaveTitle({ name: 'title', value: currentTitle })
                        onClose()
                    }}
                >
                    Save
                </button>
            </div>
            <div className="add-creation__content mt-16 mx-16">
                <input
                    type="text"
                    name="title"
                    placeholder="Write something about this creation..."
                    value={currentTitle}
                    onChange={(e) => setCurrentTitle(e.target.value)}
                    className="add-creation__input"
                    style={{ fontWeight: 'normal' }}
                />
                {!currentTitle.length && <p className='fs-real fw-medium fg-xtred ta-center'>{'Title is required'}</p>}
            </div>
        </div>
    )
}

const Credits = ({ creation, handleSaveCredits, onClose }) => {

    const [currentCredits, setCurrentCredits] = useState(creation?.credits)
    const [errorCredits, setErrorCredits] = useState('')
    const creditsTextareaRef = useRef(null);
    
    useAutoGrowTextarea(creditsTextareaRef, currentCredits, { minLines: 3, maxLines: 10 });
    
    const handleChange = (value) => {
        if (value.length > MAXIMUM_LENGTH) {
            setErrorCredits('Maximum allowed length is 1000 characters.');
        } else (setErrorCredits(''))

        setCurrentCredits(value.slice(0, MAXIMUM_LENGTH))
    }
    return (
        <div className="add-creation__content modify-subscreen pt-40">
            <div className="d-flex aic jcc modify-header mb-8">
                <Icon alias={"chevron-left"} width={24} color="#0A0F1E" className={"p-absolute"} style={{ left: 16 }} onClick={onClose} />
                <span className="fg-black fw-bold fs-sm2">CREDITS & COPYRIGHT</span>
                <button
                    className="btn btn-sm red-btn rounded-xl fs-xxs p-fixed px-16" style={{ right: '16px' }}
                    disabled={!currentCredits || !currentCredits.length || currentCredits === creation?.credits}
                    onClick={() => {
                        handleSaveCredits({ name: 'credits', value: currentCredits })
                        onClose()
                    }}
                >
                    Save
                </button>
            </div>
            <div className="add-creation__content mt-16 mx-16">
                <div className="add-creation__copies">
                    <textarea
                        ref={creditsTextareaRef}
                        type="text"
                        name="credits"
                        placeholder="Describe contributors, copyright notes, collaborators…"
                        value={currentCredits}
                        onChange={(e) => handleChange(e.target.value)}
                        className="add-creation__input"
                        style={{ fontWeight: 'normal', resize: 'none', overflow: 'hidden' }}
                        rows="3"
                    />
                    <span className="add-creation__copies-label">{MAXIMUM_LENGTH - (currentCredits?.length || 0)}</span>
                </div>
                {errorCredits && <p className='fs-real fw-medium fg-xtred ta-center'>{errorCredits}</p>}

            </div>
        </div>
    )
}


const Hashtags = ({ creation, handleSaveHashtags, onClose }) => {

    const [currentHashtags, setCurrentHashtags] = useState(creation?.formattedTags?.length ? creation.formattedTags : [])
    const [tagValue, setTagValue] = useState('')


    // Get original tags as array for comparison
    const originalTags = creation?.formattedTags ? creation.formattedTags : [];


    const handleHashtagKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();

            if (tagValue && !currentHashtags.includes(tagValue)) {
                setCurrentHashtags(prev => [...prev, tagValue])
                setTagValue('')
                // setCurrentHashtags(prev => prev.filter(t => t !== tagValue))
            }
        }
    }

    return (
        <div className="add-creation__content modify-subscreen pt-40">
            <div className="d-flex aic jcc modify-header mb-8">
                <Icon alias={"chevron-left"} width={24} color="#0A0F1E" className={"p-absolute"} style={{ left: 16 }} onClick={onClose} />
                <span className="fg-black fw-bold fs-sm2">HASHTAGS</span>
                <button
                    className="btn btn-sm red-btn rounded-xl fs-xxs p-fixed px-16" style={{ right: '16px' }}
                    disabled={!currentHashtags.length || areTagsEqual(currentHashtags, originalTags)}
                    onClick={() => { handleSaveHashtags({ name: 'formattedTags', value: currentHashtags }); onClose() }}
                >
                    Save
                </button>
            </div>
            <div className="add-creation__content mt-16 mx-16">
                <input
                    type="text"
                    name="hashtags"
                    placeholder="Hashtags..."
                    value={tagValue}
                    onChange={(e) => setTagValue(e.target.value)}
                    onKeyPress={handleHashtagKeyPress}
                    className="add-creation__input"
                />
                {!currentHashtags.length && <p className='fs-real fw-medium fg-xtred ta-center'>{'Hashtags are required'}</p>}
                <div className="hashtags-container">
                    {currentHashtags.map(tag => (
                        <span key={tag} className="hashtag fs-tiny fw-extra-bold fg-tag-blue d-flex aic">#{tag} <Icon alias="cross_v2" className="ml-8" width={20} stroke="#4EB9E8" onClick={() => setCurrentHashtags(prev => prev.filter(t => t !== tag))} /></span>
                    ))}
                </div>
            </div>
        </div>
    )
}

const Description = ({ creation, handleSaveDescription, onClose }) => {

    const [currentDescription, setCurrentDescription] = useState(creation?.description)

    const onSaveDescription = () => {
        handleSaveDescription({ name: 'description', value: currentDescription })
        onClose()
    }

    return (
        <div className="add-creation__content modify-subscreen pt-40">
            <div className="d-flex aic jcc modify-header mb-8">
                <Icon alias={"chevron-left"} width={24} color="#0A0F1E" className={"p-absolute"} style={{ left: 16 }} onClick={onClose} />
                <span className="fg-black fw-bold fs-sm2">DESCRIPTION</span>
                <button
                    className="btn btn-sm red-btn rounded-xl fs-xxs p-fixed px-16" style={{ right: '16px' }}
                    disabled={!currentDescription.length || currentDescription === creation?.description}
                    onClick={() => { onSaveDescription({ name: 'description', value: currentDescription }); onClose() }}
                >
                    Save
                </button>
            </div>
            <div className="add-creation__content mt-16 mx-16">
                <NewsEditor
                    className="ml--16"
                    value={currentDescription}
                    onChange={(value) => setCurrentDescription(value)}
                    placeholder={{
                        title: 'Write something about this creation...',
                        body: 'Describe your creation, its inspiration, or what makes it special...'
                    }}
                />

                {/* <input
                    type="text"
                    name="description"
                    placeholder="Description..."
                    value={currentDescription}
                    onChange={(e) => setCurrentDescription(e.target.value)}
                    className="add-creation__input"
                /> */}
            </div>
        </div>
    )
}

const Price = ({ creation, handleSavePrice, onClose }) => {
    // Fee structure constants (imported from globals)
    const DECIMAL_PLACES = 2 // Standard currency decimal precision
    const CENTS_MULTIPLIER = 100 // For rounding to 2 decimal places

    // Simplified combined fee multiplier
    // Formula: fullPrice = creatorPrice × (1 + HYK_FEE_PERCENT) × (1 + STRIPE_FEE_PERCENT) + STRIPE_FIXED_FEE
    // Using simplified coefficient for easier calculations
    const COMBINED_FEE_MULTIPLIER = 1.115 // Approximates (1.10 × 1.015) = 1.11665

    // creation.price is the full price (total amount)
    // Calculate creator price by reverse engineering from full price
    const calculateCreatorPriceFromFull = (fullPrice) => {
        if (!fullPrice || fullPrice === 0) return 0
        // Reverse formula: creatorPrice = (fullPrice - STRIPE_FIXED_FEE) / COMBINED_FEE_MULTIPLIER
        const rawCreatorPrice = (fullPrice - STRIPE_FIXED_FEE) / COMBINED_FEE_MULTIPLIER
        // Round up to ensure creator gets at least this amount
        return Math.ceil(rawCreatorPrice * CENTS_MULTIPLIER) / CENTS_MULTIPLIER
    }

    const initialFullPrice = creation?.price ? parseFloat(creation.price) : 0
    const initialCreatorPrice = initialFullPrice > 0 ? calculateCreatorPriceFromFull(initialFullPrice) : 0

    const [creatorPrice, setCreatorPrice] = useState(parseFloat(initialCreatorPrice.toFixed(2)))
    const [fullPrice, setFullPrice] = useState(parseFloat(initialFullPrice.toFixed(2)))
    const [free, setFree] = useState(creation?.price ? false : true)
    const [showPriceDetails, setShowPriceDetails] = useState(false)

    const onSavePrice = () => {
        // Save the full price (total amount) with exactly 2 decimal places
        const finalPrice = typeof fullPrice === 'number' ? parseFloat(fullPrice.toFixed(2)) : 0
        handleSavePrice({ name: 'price', value: finalPrice })
        onClose()
    }

    const handleCreatorPriceChange = (e) => {
        const newCreatorPrice = e.target.value === '' ? '' : parseFloat(e.target.value)
        setCreatorPrice(newCreatorPrice)
        if (newCreatorPrice !== '' && !isNaN(newCreatorPrice)) {
            const calculated = calculateFullPrice(newCreatorPrice)
            setFullPrice(parseFloat(calculated?.totalAmount) || 0)
        }
    }

    const handleFullPriceChange = (e) => {
        const newFullPrice = e.target.value === '' ? '' : parseFloat(e.target.value)
        setFullPrice(newFullPrice)

        if (newFullPrice !== '' && !isNaN(newFullPrice)) {
            // Reverse calculate creator price from full price (already rounded up)
            const estimatedCreatorPrice = calculateCreatorPriceFromFull(newFullPrice)
            setCreatorPrice(estimatedCreatorPrice)
        }
    }

    return (
        <div className="add-creation__content modify-subscreen pt-40">

            <div className="d-flex aic jcc modify-header mb-8">
                <Icon alias={"chevron-left"} width={24} color="#0A0F1E" className={"p-absolute"} style={{ left: 16 }} onClick={onClose} />
                <span className="fg-black fw-bold fs-sm2">PRICE</span>
                <button
                    className="btn btn-sm red-btn rounded-xl fs-xxs p-fixed px-16" style={{ right: '16px' }}
                    disabled={(!fullPrice && !free) || fullPrice === creation?.price}
                    onClick={() => { onSavePrice(); onClose() }}
                >
                    Save
                </button>
            </div>
            <div className="add-creation__content mt-32 mx-16">
                <div className="cultural-areas d-flex f-column aic jcc">
                    <div className="d-flex aic jcc area-container w-full">
                        <span className={`area price fs-real fw-bold ${free ? 'bg-dark fg-white' : 'bg-white fg-dark'}`} onClick={() => { setFree(true); setCreatorPrice(0); setFullPrice(0) }}>Free</span>
                        <span className={`area price fs-real fw-bold ${free ? 'bg-white fg-dark' : 'bg-dark fg-white'}`} onClick={() => { setFree(false); setCreatorPrice(0); setFullPrice(0) }}>Fixed Price</span>
                    </div>
                    {!free && (
                        <Fragment>
                            <div className="add-creation__price-input">
                                <input
                                    type="number"
                                    name="creatorPrice"
                                    placeholder="Creation price"
                                    value={creatorPrice}
                                    onChange={handleCreatorPriceChange}
                                    className="add-creation__input"
                                    step="0.01"
                                    min="0"
                                />
                                <span className="add-creation__currency">€</span>
                            </div>

                            <div className="add-creation__price-input">
                                <input
                                    type="number"
                                    name="fullPrice"
                                    placeholder="Full price with fees"
                                    value={fullPrice}
                                    onChange={handleFullPriceChange}
                                    className="add-creation__input fg-pink fw-bold"
                                    step="0.01"
                                    min="0"
                                />
                                <span className="add-creation__currency fg-pink">€</span>
                            </div>

                            <Fragment>
                                <span
                                    className="fg-pink fw-medium d-flex aic fs-regular fw-bold price-info-clickable"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setShowPriceDetails(true)}
                                    aria-label="View detailed price breakdown"
                                >
                                    price details
                                    <Icon alias={"info_v2"} color="#FF8CDC" width={16} className="ml-4" />
                                </span>
                            </Fragment>
                        </Fragment>
                    )}
                </div>
            </div>
            {showPriceDetails && <PriceDetailsAddCreation onClose={() => setShowPriceDetails(false)} price={creatorPrice} />}
        </div>
    )
}

const Availability = ({ creation, handleSaveAvailability, onClose }) => {
    const media = getMedia(creation?.media);
    const [currentAvailability, setCurrentAvailability] = useState(creation?.edition_type)
    const [copies, setCopies] = useState(media?.max_stock ? media?.max_stock : '')
    const onSaveAvailability = () => {
        handleSaveAvailability({ name: 'edition_type', value: currentAvailability })
        handleSaveAvailability({ name: 'max_stock', value: copies })
        onClose()
    }

    return (
        <div className="add-creation__content modify-subscreen pt-40">
            <div className="d-flex aic jcc modify-header mb-8">
                <Icon alias={"chevron-left"} width={24} color="#0A0F1E" className={"p-absolute"} style={{ left: 16 }} onClick={onClose} />
                <span className="fg-black fw-bold fs-sm2">QUANTITY</span>
                <button
                    className="btn btn-sm red-btn rounded-xl fs-xxs p-fixed px-16" style={{ right: '16px' }}
                    disabled={!currentAvailability || currentAvailability === creation?.edition_type}
                    onClick={() => { onSaveAvailability({ name: 'edition_type', value: currentAvailability }); onClose() }}
                >
                    Save
                </button>
            </div>
            <div className="add-creation__content mt-16 mx-16">
                <div className="cultural-areas d-flex f-column aic jcc mt-16">
                    <div className="d-flex aic jcc area-container w-full">
                        <span
                            className={`area price fs-real fw-bold ${currentAvailability === 'unlimited' ? 'bg-dark fg-white' : 'bg-white fg-dark'}`}
                            onClick={() => setCurrentAvailability('unlimited')}
                        >Unlimited</span>
                        <span
                            className={`area price fs-real fw-bold ${currentAvailability === 'limited' ? 'bg-dark fg-white' : 'bg-white fg-dark'}`}
                            onClick={() => setCurrentAvailability('limited')}
                        >Limited</span>
                        <span
                            className={`area price fs-real fw-bold ${currentAvailability === 'unique' ? 'bg-dark fg-white' : 'bg-white fg-dark'}`}
                            onClick={() => setCurrentAvailability('unique')}
                        >Unique</span>
                    </div>
                    {currentAvailability === 'limited' && (
                        <input
                            type="number"
                            name="copies"
                            placeholder="Quantity"
                            value={copies}
                            onChange={(e) => setCopies(e.target.value)}
                            className="add-creation__input"
                        />
                    )}
                </div>
            </div>
        </div>
    )
}

const ModifyCreation = () => {
    // const tags = useSelector(state => state.heatmap?.tags)
    const { creation: init_creation, tags, user_id } = useSelector(state => ({
        tags: state.heatmap?.tags,
        creation: state.creation.creation,
        user_id: state.auth?.user?.user_id
    }))

    const { creation_id } = useParams();
    const navigate = useNavigate();

    const [creation, setCreation] = useState();
    const [loading, setLoading] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [error, setError] = useState({});
    const [openEditInput, setOpenEditInput] = useState(false);
    
    // Physical creation fields state
    const [sizeEntries, setSizeEntries] = useState([]);
    const [selectedShippingMethodId, setSelectedShippingMethodId] = useState(null);
    const [isSizingAndQuantitiesEnabled, setIsSizingAndQuantitiesEnabled] = useState(false);
    const [showShippingMethodSelector, setShowShippingMethodSelector] = useState(false);
    const [allowDownload, setAllowDownload] = useState(false);


    const dispatch = useDispatch()

    const initMedia = getMedia(init_creation?.media);
    const edition_type = typeof initMedia?.max_stock === "number" && initMedia?.max_stock > 0 ? initMedia?.max_stock > 1 ? "limited" : "unique" : "unlimited"

    useEffect(() => {
        if (
            init_creation &&
            String(init_creation.creation_id) === String(creation_id) &&
            tags?.length > 0
        ) {
            const media = getMedia(init_creation?.media);
            const isPhysical = media?.media_format === 'physical';
            
            setCreation({
                ...init_creation,
                formattedTags: init_creation?.tags?.filter(tag => !tag.is_primary).map(tag => tag.name),
                edition_type: typeof media?.max_stock === "number" && media?.max_stock > 0 ? media?.max_stock > 1 ? "limited" : "unique" : "unlimited",
                price: init_creation?.price || 0,
                max_stock: media?.max_stock || 0,
            });
            
            // Initialize physical creation fields
            if (isPhysical) {
                // Initialize size entries from creation_sizes
                if (init_creation?.creation_sizes && init_creation.creation_sizes.length > 0) {
                    const sizes = init_creation.creation_sizes.map(size => ({
                        size_label: size.size_label || '',
                        stock_quantity: size.stock_quantity || ''
                    }));
                    setSizeEntries(sizes);
                    setIsSizingAndQuantitiesEnabled(true);
                } else if (init_creation?.category === 'clothing') {
                    // If clothing but no sizes, initialize with empty entry
                    setSizeEntries([{ size_label: '', stock_quantity: '' }]);
                    setIsSizingAndQuantitiesEnabled(false);
                }
                
                // Initialize shipping method
                if (init_creation?.shipping_method_id) {
                    setSelectedShippingMethodId(init_creation.shipping_method_id);
                }
            }
            
            // Initialize download setting (for digital creations)
            if (!isPhysical && media?.allow_download !== undefined) {
                setAllowDownload(media.allow_download);
            }
        }
    }, [tags, init_creation])

    useEffect(() => {
        if (creation_id) {
            dispatch(fetchCreationById(creation_id))
        }
    }, [dispatch, creation_id])

    useEffect(() => {
        if (!(tags && tags.length > 0)) {
            dispatch(fetchHeatmapTags())
        }
    }, [dispatch, tags])

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const tags = creation.formattedTags
                ? creation.formattedTags.map(tag => {
                    const trimmedTag = tag.trim().replace(/^#/, '');
                    return trimmedTag;
                }).filter(Boolean)
                : [];

            const creationMedia = getMedia(creation?.media);
            const initCreationMedia = getMedia(init_creation?.media);
            const isPhysical = creationMedia?.media_format === 'physical';
            
            // Prepare size entries payload
            const sizesPayload = isPhysical && isSizingAndQuantitiesEnabled && sizeEntries.length > 0
                ? sizeEntries
                    .filter(entry => entry.size_label && entry.size_label.trim() !== '')
                    .map(entry => ({
                        size_label: entry.size_label.trim(),
                        stock_quantity: entry.stock_quantity ? parseInt(entry.stock_quantity, 10) : null
                    }))
                : [];
            
            const obj = {
                ...(creation.title !== init_creation?.title && { title: creation.title }),
                ...(!areTagsEqual(tags.map(tag => tag.name), init_creation?.tags.map(tag => tag.name)) || creation.tag !== init_creation?.tag) && {
                    tags: tags,
                    tag: creation.tag
                },
                // ...(creation.tag !== init_creation?.tag && { tag: creation.tag }),
                ...(creation.description !== init_creation?.description && { description: creation.description }),
                ...(creationMedia?.media_format !== initCreationMedia?.media_format && { format: creationMedia?.media_format }),
                ...(creation.price !== init_creation?.price && { price: creation.price }),
                ...(creation.edition_type !== edition_type && { edition_type: creation.edition_type }),
                ...(creationMedia?.max_stock !== initCreationMedia?.max_stock && { max_stock: creationMedia?.max_stock }),
                ...(creation.credits !== init_creation?.credits && { credits: creation.credits }),
                // Download setting (for digital creations)
                ...(!isPhysical && allowDownload !== (initCreationMedia?.allow_download || false) && {
                    allow_download: allowDownload
                }),
                // Physical creation fields
                ...(isPhysical && {
                    ...(creation.category === 'clothing' && {
                        sizes: sizesPayload
                    }),
                    ...(selectedShippingMethodId && selectedShippingMethodId !== init_creation?.shipping_method_id && {
                        shipping_method_id: selectedShippingMethodId
                    })
                })
            }
            if (previewFile) {
                const fileExt = previewFile.name.split('.').pop();
                const fileName = `${user_id}_${Date.now()}.${fileExt}`;
                const filePath = `creations-thumbnail/${user_id}/${fileName}`;

                const { error: previewError } = await supabase.storage
                    .from("creations-files")
                    .upload(filePath, previewFile, { contentType: previewFile.type });

                if (!previewError) {
                    const newThumbnailUrl = await supabase.storage
                        .from('creations-files')
                        .getPublicUrl(filePath).data.publicUrl;
                    
                    obj.thumbnail_url = newThumbnailUrl;
                    
                    // Optimistic update: Update UI immediately with new thumbnail
                    const previousThumbnailUrl = creation?.thumbnail_url || init_creation?.thumbnail_url || null;
                    dispatch({
                        type: 'OPTIMISTIC_UPDATE_CREATION_THUMBNAIL',
                        payload: {
                            creation_id: creation.creation_id,
                            thumbnail_url: newThumbnailUrl,
                            previous_thumbnail_url: previousThumbnailUrl
                        }
                    });
                } else {
                    alert('An error occurred while publishing your creation. Please try again.');
                    setLoading(false);
                    return;
                }
            }

            await updateCreation(creation.creation_id, obj).then(({ success }) => {
                if (success) {
                    // Invalidate both user's creations list and the specific creation detail cache
                    cachingService.invalidate([
                        `/api/creations/${user_id}`,
                        `/api/creations/detail/${creation.creation_id}`
                    ], true);
                    dispatch(reloadCreation(true))
                    onClose();
                } else {
                    // Rollback optimistic update if API call failed
                    if (previewFile && obj.thumbnail_url) {
                        dispatch({
                            type: 'ROLLBACK_CREATION_THUMBNAIL',
                            payload: {
                                creation_id: creation.creation_id
                            }
                        });
                    }
                    alert('An error occurred while updating your creation. Please try again.');
                }
            }).catch((error) => {
                console.error('Error updating creation:', error);
                // Rollback optimistic update on error
                if (previewFile && obj.thumbnail_url) {
                    dispatch({
                        type: 'ROLLBACK_CREATION_THUMBNAIL',
                        payload: {
                            creation_id: creation.creation_id
                        }
                    });
                }
                alert('An error occurred while updating your creation. Please try again.');
            });
        } catch (error) {
            console.error('Error updating creation:', error);
            // Rollback optimistic update on error
            if (previewFile) {
                dispatch({
                    type: 'ROLLBACK_CREATION_THUMBNAIL',
                    payload: {
                        creation_id: creation.creation_id
                    }
                });
            }
        } finally {
            setLoading(false);
        }
    }
    const handleChange = (e) => {
        const { name, value } = e

        if (name === 'availability') {
            const newEdition = value;
            setCreation(c => ({
                ...c,
                edition_type: newEdition,
                // Reset max_stock to 0 when switching to unlimited
            }))
        } else if (name === 'max_stock') {
            setCreation(c => ({
                ...c,
                media: {
                    ...c.media,
                    max_stock: value
                }
            }))
        } else {
            setCreation(c => ({ ...c, [name]: value }))
        }
    }

    const handleDeleteMedia = () => {
        setCreation(c => ({ ...c, thumbnail_url: null }))
        setPreviewImage(null)
        setPreviewFile(null)
    }

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 1024 * 1024 * 1024) {
            setError(prev => ({
                ...prev, media: <span>Your file exceeds 1GB or is not a supported format. <br /> Please try again with another file</span>
            }));
            return;
        }

        const previewUrl = URL.createObjectURL(file);

        setError(prev => ({ ...prev, thumbnail_url: null }));
        setPreviewFile(file);
        setPreviewImage(previewUrl);

    }


    const handleFormatChange = (e) => {
        const { name, value } = e;
        setError(prev => ({ ...prev, [name]: null }));
        if (name === 'edition') {
            if (value === 'unlimited' || value === 'limited') {
                setCreation(prev => ({
                    ...prev,
                    [name]: value,
                    copies: null
                }));
            } else if (value === 'unique') {
                setCreation(prev => ({
                    ...prev,
                    [name]: value,
                    copies: 1
                }));
            }
        } else if (name === 'format') {
            setCreation(prev => ({
                ...prev,
                media: {
                    ...prev.media,
                    media_format: value
                }
            }));
        }
    }
    
    // Size entry management functions
    const createEmptySizeEntry = () => ({ size_label: '', stock_quantity: '' });
    
    const handleAddSizeEntry = () => {
        setSizeEntries(prev => [...prev, createEmptySizeEntry()]);
    };
    
    const handleRemoveSizeEntry = (index) => {
        setSizeEntries(prev => prev.filter((_, i) => i !== index));
    };
    
    const handleSizeEntryChange = (index, field, value) => {
        setSizeEntries(prev => prev.map((entry, i) => i === index ? {
            ...entry,
            [field]: field === 'stock_quantity' ? (value === '' ? '' : value.replace(/[^0-9]/g, '')) : value
        } : entry));
    };
    
    const handleToggleSizing = (enabled) => {
        setIsSizingAndQuantitiesEnabled(enabled);
        if (enabled && sizeEntries.length === 0) {
            setSizeEntries([createEmptySizeEntry()]);
        }
        if (!enabled) {
            setSizeEntries([createEmptySizeEntry()]);
        }
    };
    
    const handleShippingMethodSelect = (methodId) => {
        setSelectedShippingMethodId(methodId);
        setShowShippingMethodSelector(false);
    };
    
    const handleOpenShippingMethodSelector = () => {
        setShowShippingMethodSelector(true);
    };
    const previousPath = usePreviousLocation();

    const onClose = () => {
        handleGoBackRedirection(navigate, previousPath)
    }

    const media = getMedia(creation?.media);
    const isPhysical = media?.media_format === 'physical';
    const { methods: shippingMethods } = useSelector(state => state.shippingMethods || { methods: [] });
    
    // Helper function to format shipping method display text
    const formatShippingMethodText = (method) => {
        if (!method) return null;
        const title = method.title || (method.type === 'general' ? 'General' : 'Specific');
        const destinations = method.destinations || [];
        const destinationText = destinations.length > 0 
            ? (destinations.length <= 2 
                ? destinations.map(d => d.country_name || d.name || d).join(', ')
                : `${destinations.slice(0, 2).map(d => d.country_name || d.name || d).join(', ')} + ${destinations.length - 2} more`)
            : '';
        const rates = method.rates || [];
        const rateText = rates.length > 0 
            ? ` - €${parseFloat(rates[0]?.rate || 0).toFixed(2)}`
            : '';
        return `${title}${destinationText ? ` (${destinationText})` : ''}${rateText}`;
    };
    
    // Get shipping method display text - prioritize creation's shipping_method object
    const getShippingMethodDisplayText = () => {
        if (!selectedShippingMethodId) {
            return 'Select shipping method';
        }
        
        // First, try to get from creation's shipping_method object (most reliable)
        const creationMethod = init_creation?.shipping_method || creation?.shipping_method;
        if (creationMethod) {
            const formatted = formatShippingMethodText(creationMethod);
            if (formatted) return formatted;
        }
        
        // Second, try to find in Redux shippingMethods array
        const selectedShippingMethod = shippingMethods.find(m => 
            (m.shipping_method_id || m.id) === selectedShippingMethodId
        );
        if (selectedShippingMethod) {
            const formatted = formatShippingMethodText(selectedShippingMethod);
            if (formatted) return formatted;
        }
        
        // Fallback: if we have an ID but no method data, show generic message
        return 'Shipping method selected';
    };
    
    const selectedShippingMethodName = getShippingMethodDisplayText();

    return (
        <>
            {!openEditInput ?
                <MainEdit
                    creation={creation}
                    onClose={onClose}
                    handleSubmit={handleSubmit}
                    loading={loading}
                    handleFileChange={handleFileChange}
                    previewImage={previewImage}
                    error={error}
                    setOpenEditInput={setOpenEditInput}
                    capitalizeFirstLetter={capitalizeFirstLetter}
                    tags={tags}
                    handleChange={handleChange}
                    handleDeleteMedia={handleDeleteMedia}
                    isPhysical={isPhysical}
                    sizeEntries={sizeEntries}
                    isSizingAndQuantitiesEnabled={isSizingAndQuantitiesEnabled}
                    selectedShippingMethodId={selectedShippingMethodId}
                    selectedShippingMethodName={selectedShippingMethodName}
                    onOpenShippingMethodSelector={handleOpenShippingMethodSelector}
                    allowDownload={allowDownload}
                    setAllowDownload={setAllowDownload}
                />
                : openEditInput === "format" ?
                    <EditFormat key="format" creation={creation} handleSaveFormat={handleFormatChange} onClose={() => setOpenEditInput(null)} />
                    : openEditInput === "cultural-area" ?
                        <CulturalArea key="cultural-area" creation={creation} tags={tags} handleSaveCulturalArea={handleChange} onClose={() => setOpenEditInput(null)} />
                        : openEditInput === "title" ?
                            <Title key="title" creation={creation} handleSaveTitle={handleChange} onClose={() => setOpenEditInput(null)} />
                            : openEditInput === "hashtags" ?
                                <Hashtags key="hashtags" creation={creation} handleSaveHashtags={handleChange} onClose={() => setOpenEditInput(null)} />
                                : openEditInput === "description" ?
                                    <Description key="description" creation={creation} handleSaveDescription={handleChange} onClose={() => setOpenEditInput(null)} />
                                    : openEditInput === "price" ?
                                        <Price key="price" creation={creation} handleSavePrice={handleChange} onClose={() => setOpenEditInput(null)} />
                                        : openEditInput === "availability" ?
                                            <Availability key="availability" creation={creation} handleSaveAvailability={handleChange} onClose={() => setOpenEditInput(null)} />
                                            : openEditInput === 'credits' ?
                                                <Credits key="credits" creation={creation} handleSaveCredits={handleChange} onClose={() => setOpenEditInput(null)} />
                                                : openEditInput === 'sizes' ?
                                                    <EditSizes 
                                                        key="sizes" 
                                                        creation={creation}
                                                        sizeEntries={sizeEntries}
                                                        isSizingAndQuantitiesEnabled={isSizingAndQuantitiesEnabled}
                                                        onSave={(entries, enabled) => {
                                                            setSizeEntries(entries);
                                                            setIsSizingAndQuantitiesEnabled(enabled);
                                                            setOpenEditInput(null);
                                                        }}
                                                        onClose={() => setOpenEditInput(null)}
                                                        onAddSize={handleAddSizeEntry}
                                                        onRemoveSize={handleRemoveSizeEntry}
                                                        onSizeChange={handleSizeEntryChange}
                                                        onToggleSizing={handleToggleSizing}
                                                    />
                                                    : null
            }
            {showShippingMethodSelector && (
                <ShippingMethodSelector
                    selectedMethodId={selectedShippingMethodId}
                    onSelect={handleShippingMethodSelect}
                    onClose={() => setShowShippingMethodSelector(false)}
                />
            )}
        </>
    )
}

export default ModifyCreation